import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useResourceKit } from '@resource-kit/core/react';
import type { AnyFieldDef, Issue, RecordNode } from '@resource-kit/model';
import { resourceKeys } from '../../query/keys';
import { resolveViewModifiers } from '../../modifiers';
import type { ResourceBundle } from '../bundle';
import type { EntityField } from '../contracts';
import { viewFieldOf } from './viewField';
import {
  buildCreatePayload,
  buildUpdatePayload,
  commitRecordSave,
  createRecordNode,
  discardEdits,
  isRecordDirty,
  mergeServerSnapshot,
  setFieldValue,
  setIssues,
  type FormFieldSpec,
} from '../../form/record-node';
import {
  GENERIC_ISSUE_KEY,
  issuesForField,
  runRecordValidator,
  validateDraft,
  validateFieldValue,
  type ViewFieldOverride,
} from '../../form/validation';

/**
 * `saving` is derived, not stored: it is exactly "a save mutation is in
 * flight", and storing it separately invites the two to disagree.
 */
export type FormMode = 'readonly' | 'edit' | 'saving';

export interface UseFormViewArgs<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  /** Server snapshot. `null` starts an unsaved record. */
  record: T | null;
  recordId?: string;
  /** Prefill for an unsaved record (an action's `context`). */
  initialValues?: Partial<T>;
  /** `create` opens straight into edit and has no readonly state to return to. */
  intent?: 'record' | 'create';
  canEdit?: boolean;
  /**
   * Validation layer 3 — per-item view overrides, keyed by field name. A view
   * may TIGHTEN a field (a `required` that only applies on this form); it can
   * never loosen the model's own rule.
   */
  viewOverrides?: Readonly<Record<string, ViewFieldOverride>>;
  onSaved?: (saved: T) => void;
  /** Called after a successful create, and by Cancel on a create form. */
  onCloseCreate?: () => void;
  /**
   * Turn a failed save into the two things this hook does with it: the field
   * issues to route, and the form-level message to show when there are none.
   *
   * REQUIRED, and injected rather than imported (#2167 PR-3). Both halves used
   * to be host calls — `normalizeBackendIssues` reads `ApiError`, this app's
   * HTTP client's error class, and `resolveApiError` resolves an i18n key
   * NAMESPACED BY RESOURCE against this app's catalogs. Neither is a fact a
   * package can know: what a failed request looks like on the wire, and how a
   * host names the copy for it, are the host's (ADR-0035 D1).
   *
   * One call per failure, deliberately: `issues` and `message` describe the
   * SAME error, and calling twice would let a stateful describer answer
   * inconsistently.
   */
  describeError: (error: unknown) => { message: string; issues: Issue<T>[] };
}

export interface FormViewController<T> {
  mode: FormMode;
  node: RecordNode<T>;
  fields: readonly EntityField<T>[];
  isDirty: boolean;
  canEdit: boolean;
  formError: string | null;
  enterEdit: (fieldKey?: string) => void;
  cancel: () => void;
  save: () => void;
  setValue: (fieldKey: string, value: unknown) => void;
  blurField: (fieldKey: string) => void;
  issuesFor: (fieldKey: string) => Issue<T>[];
  /** Focus token for `fieldKey`, or null when focus is not headed there. */
  focusTokenFor: (fieldKey: string) => number | null;
  isEditable: (field: EntityField<T>) => boolean;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  (value ?? {}) as Record<string, unknown>;

export function useFormView<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  record,
  recordId,
  initialValues,
  intent = 'record',
  canEdit = false,
  viewOverrides,
  onSaved,
  onCloseCreate,
  describeError,
}: UseFormViewArgs<T, F>): FormViewController<T> {
  const { model, list, host } = bundle;
  const { validate, fields: modelFields } = model;
  const { api, slug, label } = host;
  const { t, confirm, notify } = useResourceKit();
  const queryClient = useQueryClient();
  const isCreate = intent === 'create';

  // The bundle is a module-level constant — `slug` is a stable identity.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const fields = useMemo(
    () =>
      list.items.flatMap((item) => {
        const modelField = modelFields[item.field];
        const modifiers = resolveViewModifiers(item, modelField, null);
        if (modifiers.invisible) return [];
        return [
          viewFieldOf(item, modelField, {
            required: modifiers.required,
            readonly: modifiers.readonly,
          }),
        ];
      }),
    [slug],
  );

  const specs = useMemo<FormFieldSpec[]>(
    () => fields.map((f) => ({ key: f.key, type: f.type, encodeEmpty: f.encodeEmpty })),
    [fields],
  );
  const specByKey = useMemo(() => new Map(specs.map((s) => [s.key, s])), [specs]);
  const fieldByKey = useMemo(() => new Map(fields.map((f) => [String(f.key), f])), [fields]);

  const seedDraft = (source: unknown): Partial<T> => {
    const from = asRecord(source);
    const draft: Record<string, unknown> = {};
    for (const spec of specs) draft[spec.key] = from[spec.key];
    return draft as Partial<T>;
  };

  const [node, setNode] = useState<RecordNode<T>>(() =>
    createRecordNode<T>({
      id: isCreate ? null : (recordId ?? null),
      initial: isCreate ? null : record,
      draft: seedDraft(isCreate ? initialValues : record),
    }),
  );
  const [mode, setMode] = useState<'readonly' | 'edit'>(isCreate ? 'edit' : 'readonly');
  const [focus, setFocus] = useState<{ key: string; token: number } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const focusCounter = useRef(0);
  // §6.3: after a successful save focus returns to the field CONTAINER the
  // operator was last in — never to <body>.
  const lastTouchedKey = useRef<string | null>(null);

  // A detail fetch that resolves while the operator is already typing must
  // fill the untouched fields WITHOUT clobbering the in-flight edit.
  useEffect(() => {
    if (isCreate || record === null) return;
    setNode((prev) => mergeServerSnapshot(prev, record, specs));
  }, [record, specs, isCreate]);

  // Permission (`canEdit`), the record's contract (`serverAssigned`) and
  // domain state (`readonly`) are three separate concepts (#1799, #1871) and
  // are asked separately here: the operator may edit the resource AND this
  // field is not the server's to assign AND this record's state does not
  // forbid it. The `serverAssigned` term sits OUTSIDE the `isCreate`
  // exemption on purpose — a create form has no state to consult, but it is
  // exactly where a column the server mints must still not be offered.
  const isEditable = useCallback(
    (field: EntityField<T>) =>
      canEdit && field.serverAssigned !== true && (isCreate || field.readonly !== true),
    [canEdit, isCreate],
  );

  const replaceFieldIssues = (
    current: readonly Issue<T>[],
    key: string,
    issue: Issue<T> | null,
  ): Issue<T>[] => [...current.filter((i) => i.path[0] !== key), ...(issue ? [issue] : [])];

  const setValue = useCallback(
    (fieldKey: string, value: unknown) => {
      const spec = specByKey.get(fieldKey);
      const field = fieldByKey.get(fieldKey);
      if (!spec || !field) return;
      lastTouchedKey.current = fieldKey;
      setFormError(null);
      setNode((prev) => {
        const next = setFieldValue(prev, spec, value);
        // Design §5.4: on change, re-run the field schema ONLY when the
        // field already carries an issue. Validating every keystroke
        // punishes the operator mid-typing.
        if (!prev.issues.some((i) => i.path[0] === fieldKey)) return next;
        return setIssues(
          next,
          replaceFieldIssues(
            next.issues,
            fieldKey,
            validateFieldValue(field, value, viewOverrides?.[fieldKey]),
          ),
        );
      });
    },
    [specByKey, fieldByKey, viewOverrides],
  );

  const blurField = useCallback(
    (fieldKey: string) => {
      const field = fieldByKey.get(fieldKey);
      if (!field) return;
      setNode((prev) =>
        setIssues(
          prev,
          replaceFieldIssues(
            prev.issues,
            fieldKey,
            validateFieldValue(field, asRecord(prev.draft)[fieldKey], viewOverrides?.[fieldKey]),
          ),
        ),
      );
    },
    [fieldByKey, viewOverrides],
  );

  const requestFocus = useCallback((fieldKey: string) => {
    focusCounter.current += 1;
    setFocus({ key: fieldKey, token: focusCounter.current });
  }, []);

  /**
   * Route a save-blocking issue set to the channels that actually exist
   * (#1937). BOTH blocking paths need this — the local sweep and the
   * backend's 400/422 — because neither one's `path` is constrained by
   * `fields`: a record validator's path is unconstrained by construction, and
   * a backend issue can name a required column this form never renders. An
   * issue no rendered field owns has no `FieldSlot` to draw it (`issuesFor`
   * dispatches by field key) and `requestFocus` would aim at a slot that does
   * not exist, so the operator would see the Save click do nothing at all.
   */
  const routeIssues = useCallback(
    (issues: readonly Issue<T>[]) => {
      const anchored = issues.filter((i) => fieldByKey.has(String(i.path[0])));
      const orphan = issues.find((i) => !fieldByKey.has(String(i.path[0])));
      if (anchored.length > 0) requestFocus(String(anchored[0]!.path[0]));
      if (orphan) {
        // Only the FIRST orphan, mirroring the focus rule: one blocker at a
        // time, re-reported on the next save attempt.
        //
        // `Issue.message` is an i18n key by CONVENTION, not by guarantee —
        // `validation.ts` passes zod's own English `issue.message` straight
        // through. So the fallback is the translated generic, `GENERIC_ISSUE_KEY`
        // (`packages/resource-kit-controllers/form/validation.ts::GENERIC_ISSUE_KEY`),
        // which is the very constant the field
        // channel falls back to — `FieldSlot.tsx` imports `GENERIC_ISSUE_KEY`
        // and passes it as the `defaultValue` of its issue paragraph
        // (`packages/resource-kit-ui/react/form/FieldSlot.tsx::FieldSlot`):
        // one `Issue.message` must not render two different ways, and echoing
        // raw author prose would put a user-visible string outside i18next.
        setFormError(
          t('rk.resource.validation.recordLevel', {
            message: t(orphan.message, {
              defaultValue: t(GENERIC_ISSUE_KEY),
            }),
          }),
        );
      }
    },
    [fieldByKey, requestFocus, t],
  );

  const firstEditableKey = useCallback(() => {
    const field = fields.find(isEditable);
    return field ? String(field.key) : null;
  }, [fields, isEditable]);

  const enterEdit = useCallback(
    (fieldKey?: string) => {
      if (!canEdit) return;
      setMode('edit');
      // All three entry points (value click / pencil / top button) land here:
      // the WHOLE record enters edit, focus goes to the field that was hit.
      const target = fieldKey ?? firstEditableKey();
      if (target) requestFocus(target);
    },
    [canEdit, firstEditableKey, requestFocus],
  );

  const mutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isCreate) {
        if (!api.create) {
          throw new Error(`No create API configured for ${slug}`);
        }
        return await api.create(payload as Partial<T>);
      }
      if (!api.update || !recordId) {
        throw new Error(`No update API configured for ${slug}`);
      }
      return await api.update(recordId, payload as Partial<T>);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys(slug).all() });
      const translatedLabel = t(label, { defaultValue: label });
      notify(
        'success',
        isCreate ? t('rk.resource.created') : t('rk.resource.updated'),
        isCreate
          ? t('rk.resource.createdSuccess', { name: translatedLabel })
          : t('rk.resource.updatedSuccess', { name: translatedLabel }),
      );
      if (isCreate) {
        onCloseCreate?.();
        return;
      }
      setNode((prev) => commitRecordSave(prev, response));
      setMode('readonly');
      if (lastTouchedKey.current) requestFocus(lastTouchedKey.current);
      onSaved?.(response);
    },
    onError: (error: unknown) => {
      // Backend 400/422 zod issues normalise onto the same `FieldPath`
      // and take the same channels as a frontend issue — the field's own
      // slot when one owns it, the form-level error when none does.
      const described = describeError(error);
      if (described.issues.length > 0) {
        setNode((prev) => setIssues(prev, described.issues));
        routeIssues(described.issues);
        return;
      }
      setFormError(described.message);
    },
  });

  const save = useCallback(() => {
    if (mutation.isPending) return;
    setFormError(null);
    void (async () => {
      const merged = { ...asRecord(node.initial), ...asRecord(node.draft) } as T;
      const issues = [
        // `T` is passed explicitly: `validateDraft`'s only appearance of it is
        // the return type (`Issue<T>[]`), so inference has nothing to work from
        // and would settle on `unknown`.
        ...validateDraft<T>(fields, asRecord(node.draft), viewOverrides),
        ...(await runRecordValidator(validate, merged, {
          mode: isCreate ? 'create' : 'update',
          changed: node.changed,
        })),
      ];
      if (issues.length > 0) {
        // The node keeps the FULL set — an issue is a fact about the
        // record whether or not a slot happens to draw it (#1937).
        setNode((prev) => setIssues(prev, issues));
        routeIssues(issues);
        return;
      }
      mutation.mutate(isCreate ? buildCreatePayload(node, specs) : buildUpdatePayload(node, specs));
    })();
  }, [mutation, node, fields, validate, isCreate, specs, routeIssues, viewOverrides]);

  const cancel = useCallback(() => {
    const leave = () => {
      if (isCreate) {
        onCloseCreate?.();
        return;
      }
      setNode((prev) => discardEdits(prev));
      setFormError(null);
      setMode('readonly');
    };
    if (!isRecordDirty(node)) {
      leave();
      return;
    }
    void confirm({
      title: t('rk.resource.discardTitle'),
      message: t('rk.resource.discardMessage'),
      intent: 'warning',
      confirmText: t('rk.resource.discardConfirm'),
      cancelText: t('rk.resource.keepEditing'),
    }).then((ok) => {
      if (ok) leave();
    });
  }, [confirm, isCreate, node, onCloseCreate, t]);

  const effectiveMode: FormMode = mutation.isPending ? 'saving' : mode;

  return {
    mode: effectiveMode,
    node,
    fields,
    // Design §6.2 invariant 2: `isDirty` is structurally false in readonly.
    isDirty: mode !== 'readonly' && isRecordDirty(node),
    canEdit,
    formError,
    enterEdit,
    cancel,
    save,
    setValue,
    blurField,
    issuesFor: (key) => issuesForField(node.issues, key),
    focusTokenFor: (key) => (focus?.key === key ? focus.token : null),
    isEditable,
  };
}
